/**
 * FABRICATED spend-by-expense-category figures for the card Overview tab.
 *
 * NOTHING here is real. There is no per-card spend-by-category endpoint: the
 * only category breakdown the API serves is the org-wide spend dashboard
 * (`GET /reporting/spend-dashboard?chart=spend_by_accounting_category`), which
 * is ERP-derived, has no `creditCardId` dimension, and is gated behind both a
 * Pro tier and a connected ERP integration. Scoping it to one card is a backend
 * change, not a query-param change.
 *
 * Do NOT "make this real" by summing the card's recent transactions into
 * buckets on the client. Transactions carry no GL account, the list is
 * paginated, and a plausible wrong breakdown is worse than an obviously fake
 * one.
 *
 * EX-40596 removed every other fabricated figure from the Overview tab — the
 * stats roll-up now reads real limits or renders an em dash, and the allocated
 * budget total was dropped rather than faked. This panel is the last one left,
 * and it needs a real per-card endpoint before the tab can go to a customer who
 * could mistake it for their own spend.
 *
 * Rows are ordered by spend descending so the chart reads as a ranking; the
 * residual `other` bucket is pinned last regardless of its size, matching how
 * the Reporting dashboard folds its long tail.
 *
 * AMOUNTS TOTAL EXACTLY $7,700.00. That used to be load-bearing: it matched the
 * fabricated `balance` the stats roll-up rendered two panels above this chart,
 * so the two invented figures could not contradict each other. EX-40596 deleted
 * that fallback — the roll-up now shows a real balance or an em dash — so there
 * is no sibling figure left to agree with, and the total is pinned only to the
 * design below.
 *
 * Cents are chosen so each bar's compact label matches the design exactly
 * ($3.2K / $1.8K / $1.1K / $980 / $410 / $240) while still summing to $7,700 —
 * which is why travel is 317000 rather than a round 320000.
 *
 * Labels are NOT stored here — `categoryKey` is a stable identifier the panel
 * maps to a translated label. Storing English display text in the fixture would
 * put it beyond the reach of the localization gate.
 */

export type SpendByCategoryKey =
  | 'travel'
  | 'software'
  | 'advertising'
  | 'dining'
  | 'entertainment'
  | 'other'

export type SpendByCategoryDemoRow = {
  categoryKey: SpendByCategoryKey
  /**
   * GL-style expense category code. `null` for the residual bucket, which is an
   * aggregate of several codes rather than one — the panel reads this to keep
   * the tail from ever headlining as the top category.
   */
  categoryCode: string | null
  spendCents: number
}

export const CARD_OVERVIEW_SPEND_BY_CATEGORY_DEMO: SpendByCategoryDemoRow[] = [
  { categoryKey: 'travel', categoryCode: '6100', spendCents: 317000 },
  { categoryKey: 'software', categoryCode: '6300', spendCents: 180000 },
  { categoryKey: 'advertising', categoryCode: '6500', spendCents: 110000 },
  { categoryKey: 'dining', categoryCode: '6200', spendCents: 98000 },
  { categoryKey: 'entertainment', categoryCode: '6400', spendCents: 41000 },
  { categoryKey: 'other', categoryCode: null, spendCents: 24000 },
]
